import axios from "axios";
import { Layout, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import SideCards from "./sideCards/sideCards";
import LiveStats from "./liveStats/LiveStats";
import "./homePage.css";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined
} from "@ant-design/icons";
import docMenu from "../docPage/menu.json";

const { Content } = Layout;

export function HomePage() {
  const [mirrorsList, setMirrorsList] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    (async () => {
      const response = await axios.get("/mirrors.json");

      const docs = new Set();
      docMenu.forEach(cur => {
        if (cur.name !== undefined) {
          docs.add(cur.name);
        }
      });

      const mirrorsList = response.data.map(m =>
        Object.assign(m, {
          has_doc: docs.has(m.name)
        })
      );
      mirrorsList.sort((a, b) => {
        return a.name < b.name ? -1 : 1;
      });

      setLoaded(true);
      setMirrorsList(mirrorsList);
    })();
  }, []);

  const filteredMirrors = useMemo(() => {
    if (!mirrorsList) {
      return [];
    }

    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) {
      return mirrorsList;
    }

    return mirrorsList.filter(item => {
      const combinedText = `${item.name} ${item.status} ${item.last_update}`.toLowerCase();
      return combinedText.includes(keyword);
    });
  }, [mirrorsList, searchValue]);

  return (
    <Content className="home-page">
      <div className="page">
        <header className="hero">
          {/* Sol: metin + butonlar */}
          <div className="hero__left">
            <div className="hero__badge">
              <span className="hero__badge-dot" />
              Open Source · Free · Always Up-to-Date
            </div>

            <h1 className="hero__title">
              Discover the<br />
              <span className="hero__title-accent">Mirror Archive</span>
            </h1>

            <p className="hero__desc">
              Monitor fresh mirror snapshots from a single dashboard.
              Secure, fast, and constantly updated entries keep your downloads on track.
            </p>

            <div className="hero__actions">
              <SideCards />
            </div>
          </div>

          {/* Sağ: canlı dashboard */}
          <div className="hero__right">
            <LiveStats />
          </div>
        </header>

        <main className="card">
          <div className="card__header">
            <div>
              <h2>Mirror Catalogue</h2>
              <p>Track real-time mirror health, file size, and last sync activity.</p>
            </div>
            <div className="card__tools">
              <label className="search">
                <span className="search__icon" aria-hidden="true">
                  🔍
                </span>
                <input
                  type="search"
                  placeholder="Search mirrors"
                  aria-label="Search mirrors"
                  value={searchValue}
                  onChange={event => setSearchValue(event.target.value)}
                />
              </label>
              <span className="search-count">
                Showing {filteredMirrors.length} {filteredMirrors.length === 1 ? "mirror" : "mirrors"}
              </span>
            </div>
          </div>

          <MirrorsList mirrorsList={filteredMirrors} loaded={loaded} />
        </main>

        <footer className="footer">
          <p>
            © {new Date().getFullYear()} Mirror Download Center. Validate mirror integrity regularly to ensure dependable downloads.
          </p>
        </footer>
      </div>
    </Content>
  );
}

const statusMeta = {
  success: { label: "Up to date", className: "status--success" },
  syncing: { label: "Syncing", className: "status--warning" },
  fail: { label: "Outdated", className: "status--danger" },
  failed: { label: "Outdated", className: "status--danger" }
};

const MirrorsList = ({ loaded, mirrorsList }) => {
  if (!loaded) {
    return (
      <div className="card__loading">
        <Spin size="large" />
        <span>Fetching the latest mirror data…</span>
      </div>
    );
  }

  if (!mirrorsList || mirrorsList.length === 0) {
    return (
      <div className="empty-state">
        <p>No mirrors match your search yet. Try a different keyword.</p>
      </div>
    );
  }

  return (
    <table className="mirror-table">
      <thead>
        <tr>
          <th>Mirror</th>
          <th>Freshness</th>
          <th>Size</th>
          <th>Last Update</th>
        </tr>
      </thead>
      <tbody>
        {mirrorsList.map(item => {
          const badgeVariant = item.has_doc ? "badge--primary" : "badge--alt";
          const badgeLabel = item.has_doc ? "Official" : "Community";
          const status = statusMeta[item.status] || {
            label: item.status ? item.status : "Unknown",
            className: ""
          };

          const formattedDate = (item.last_update || "")
            .split(" ")
            .slice(0, 2)
            .join(" ");

          return (
            <tr key={item.name}>
              <td className="mirror-name">
                {/* <span className={`badge ${badgeVariant}`}>{badgeLabel}</span> */}
                <a href={`/#/browse/${item.name}/`}>{item.name}</a>
                {item.has_doc && (
                  <a href={`/#/doc/${item.name}`}><QuestionCircleOutlined /></a>
                )}
              </td>
              <td>
                <span className={`status ${status.className}`}>{status.label}</span>
              </td>
              <td>{item.size || "—"}</td>
              <td>{formattedDate || "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
